import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { X, ArrowRight, Lightning } from "@phosphor-icons/react";

/**
 * First-Trade Tutorial — a 4-step onboarding walkthrough shown once
 * when the user opens Prop Challenge for the first time.
 *
 * Uses localStorage `tj_pc_tutorial_v1` so it never nags returning users.
 */

const STEPS = [
  {
    title: "Welcome to the Prop Firm Challenge",
    body: "Trade real symbols with virtual money under the exact rules a funded prop firm applies — profit target, daily loss, drawdown, min days, news lock, consistency. Zero real money at risk.",
    accent: "from-primary/30 to-primary/5",
    icon: "🎯",
  },
  {
    title: "Pick a symbol",
    body: "Tabs across the top swap the live TradingView chart AND the order form. XAUUSD, BTC, EURUSD, USOIL and 11 more — one tap.",
    accent: "from-emerald-500/30 to-emerald-500/5",
    icon: "📈",
  },
  {
    title: "Place a one-tap trade",
    body: "Enter lots on the right, watch the live price, and hit the big red Sell or blue Buy box. Add a Stop Loss to see live risk-% preview before you click.",
    accent: "from-blue-500/30 to-blue-500/5",
    icon: "⚡",
  },
  {
    title: "Watch your rules & share it",
    body: "Rule cards live-update on every tick. When you pass, download a shareable Certificate PDF and post your win — the community leaderboard shows your run.",
    accent: "from-amber-500/30 to-amber-500/5",
    icon: "🏆",
  },
];

const KEY = "tj_pc_tutorial_v1";

export default function PropTutorial({ open, onClose }) {
  const [i, setI] = useState(0);
  const step = STEPS[i];
  const last = i === STEPS.length - 1;

  useEffect(() => { if (open) setI(0); }, [open]);
  if (!open) return null;

  const finish = () => {
    try { localStorage.setItem(KEY, "1"); } catch { /* noop */ }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="pc-tutorial">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card overflow-hidden shadow-2xl">
        <div className={`relative p-8 bg-gradient-to-br ${step.accent}`}>
          <button onClick={finish}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
            data-testid="pc-tutorial-close">
            <X size={18} />
          </button>
          <div className="flex items-center gap-2 mb-4">
            <Lightning size={14} weight="fill" className="text-primary" />
            <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-semibold">
              Tradelog · Quick Tour · {i + 1} of {STEPS.length}
            </p>
          </div>
          <div className="text-5xl mb-4">{step.icon}</div>
          <h2 className="font-display text-2xl font-bold tracking-tight" data-testid="pc-tutorial-title">
            {step.title}
          </h2>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{step.body}</p>
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <div className="flex gap-1.5">
            {STEPS.map((_, k) => (
              <span key={k} className={`w-1.5 h-1.5 rounded-full transition-all ${k === i ? "bg-primary w-6" : "bg-muted-foreground/40"}`} />
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={finish} className="rounded-full text-xs" data-testid="pc-tutorial-skip">
              Skip
            </Button>
            <Button onClick={() => last ? finish() : setI(i + 1)}
              className="rounded-full"
              data-testid="pc-tutorial-next">
              {last ? "Start trading" : (<>Next <ArrowRight size={14} className="ml-1" /></>)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function shouldShowTutorial() {
  try { return !localStorage.getItem(KEY); } catch { return false; }
}
